import { Link, useRouterState } from '@tanstack/react-router';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { cx } from '@/lib/cx';
import { t } from '@/lib/i18n';
import { pathTo } from '@/lib/search';
import { Logo, Tooltip } from '@/ui';
import { isActive, useNav, type VisibleNavItem } from './nav';
import s from './Sidebar.module.css';

function NavEntry({ item, pathname, collapsed, among }: { item: VisibleNavItem; pathname: string; collapsed: boolean; among: VisibleNavItem[] }) {
  const active = isActive(item, pathname, among);
  const Icon = item.icon;
  const link = (
    <Link {...pathTo(item.to)} className={cx(s.item, active && s.active, item.planned && s.planned)} aria-current={active ? 'page' : undefined}>
      <Icon className={s.icon} aria-hidden />
      <span className={s.label}>{t(item.label)}</span>
      {item.planned && !collapsed && <span className={s.soon}>{t('Soon')}</span>}
    </Link>
  );
  return (
    <li>
      {collapsed ? (
        <Tooltip content={t(item.label)} side="right">
          {link}
        </Tooltip>
      ) : (
        link
      )}
      {active && !collapsed && item.children.length > 0 && (
        <ul className={s.children}>
          {item.children.map((c) => {
            const on = isActive(c, pathname, item.children);
            return (
              <li key={c.key}>
                <Link {...pathTo(c.to)} className={cx(s.child, on && s.childActive)} aria-current={on ? 'page' : undefined}>
                  {t(c.label)}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

export function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle?: () => void }) {
  const { primary, more, all } = useNav();
  const pathname = useRouterState({ select: (st) => st.location.pathname });
  return (
    <nav className={cx(s.sidebar, collapsed && s.collapsed)} aria-label={t('Main navigation')}>
      <div className={s.brand}>
        <Link to="/" className={s.brandLink} aria-label={t('Home')}>
          <Logo variant={collapsed ? 'mark' : 'full'} />
        </Link>
      </div>
      <div className={s.scroll}>
        <ul className={s.list}>
          {primary.map((item) => (
            <NavEntry key={item.key} item={item} pathname={pathname} collapsed={collapsed} among={all} />
          ))}
        </ul>
        {more.length > 0 && (
          <>
            <div className={s.section}>{collapsed ? <span className={s.sectionRule} /> : t('More')}</div>
            <ul className={s.list}>
              {more.map((item) => (
                <NavEntry key={item.key} item={item} pathname={pathname} collapsed={collapsed} among={all} />
              ))}
            </ul>
          </>
        )}
      </div>
      {onToggle && (
        <div className={s.foot}>
          <button type="button" className={s.toggle} onClick={onToggle} aria-label={collapsed ? t('Expand navigation') : t('Collapse navigation')}>
            {collapsed ? <PanelLeftOpen aria-hidden /> : <PanelLeftClose aria-hidden />}
            {!collapsed && <span>{t('Collapse')}</span>}
          </button>
        </div>
      )}
    </nav>
  );
}
